import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { BackrsUser } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, SxProps, Theme } from '@mui/material';

import { BillingPage } from './BillingPage';
import { AccountSettings, AccountSettingsRef } from './molecules/AccountSettings';
import { SettingsHeader } from './molecules/SettingsHeader';
import { SeatsTab } from './SeatsTab';
import { SEATS_TAB, SETTINGS_TAB_PARAM } from './settingsTabs';
import { FamilyPageContainer } from '../Family/FamilyPageContainer';

const seatsStrings = appStrings.modules.accountSettings.seats;

export const TABS = {
  Settings: 0,
  Seats: 1,
  Billing: 2,
  Family: 3,
} as const;

type TabIndex = (typeof TABS)[keyof typeof TABS];

type TabConfig = Readonly<{
  id: string;
  label: string;
  disabled: boolean;
}>;

export type TabsConfig = Readonly<{
  tabs: TabConfig[];
  defaultTab: TabIndex;
}>;

type SettingsPageProps = Readonly<{
  user: BackrsUser;
  enableBillingTab: boolean;
  enableFamilyTab: boolean;
  enableSeatsTab: boolean;
}>;

export const SettingsPage = ({
  user,
  enableBillingTab,
  enableFamilyTab,
  enableSeatsTab,
}: SettingsPageProps) => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabIndex>(TABS.Settings);

  // The permission behind the Seats tab answers after the first render.
  useEffect(() => {
    if (!enableSeatsTab || searchParams.get(SETTINGS_TAB_PARAM) !== SEATS_TAB) return;

    setActiveTab(TABS.Seats);
  }, [enableSeatsTab, searchParams]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSaveError, setFormSaveError] = useState<string | null>(null);
  const accountSettingsRef = useRef<AccountSettingsRef>(null);

  const getTabConfiguration = (): TabsConfig => {
    const tabs: TabConfig[] = [
      {
        id: 'settings',
        label: 'Settings',
        disabled: false,
      },
      {
        id: SEATS_TAB,
        label: seatsStrings.tabLabel,
        disabled: !enableSeatsTab,
      },
      {
        id: 'billing',
        label: 'Billing',
        disabled: !enableBillingTab,
      },
      {
        id: 'family',
        label: 'Family',
        disabled: !enableFamilyTab,
      },
      {
        id: 'networks',
        label: 'Networks',
        disabled: true,
      },
      {
        id: 'notifications',
        label: 'Notifications',
        disabled: true,
      },
      {
        id: 'connections',
        label: 'Connections',
        disabled: true,
      },
    ];

    return {
      tabs,
      defaultTab: TABS.Settings,
    };
  };

  const tabConfig = getTabConfiguration();

  const handleFormChange = (submitting: boolean, error: string | null) => {
    setIsSubmitting(submitting);
    setFormSaveError(error);
  };

  const handleReset = () => {
    accountSettingsRef.current?.resetForm();
  };

  const handleSubmit = () => {
    accountSettingsRef.current?.submitForm();
  };

  const returnToDefaultTab = () => {
    setActiveTab(TABS.Settings);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case TABS.Settings:
        return (
          <AccountSettings ref={accountSettingsRef} user={user} onFormChange={handleFormChange} />
        );
      case TABS.Billing:
        return <BillingPage />;
      case TABS.Family:
        return <FamilyPageContainer returnToDefaultTab={returnToDefaultTab} />;
      case TABS.Seats:
        return <SeatsTab />;
      default:
        return null;
    }
  };

  return (
    <Box sx={pageContainerSx}>
      <Box sx={containerSx}>
        {/* HEADER */}
        <SettingsHeader
          activeTab={activeTab}
          tabConfig={tabConfig}
          setActiveTab={(tab: number) => setActiveTab(tab as TabIndex)}
          onReset={activeTab === TABS.Settings ? handleReset : undefined}
          onSubmit={activeTab === TABS.Settings ? handleSubmit : undefined}
          hideSectionHeader={activeTab === TABS.Family}
          isLoading={isSubmitting}
          formSaveError={formSaveError}
        />

        {/* TAB CONTENT */}
        {renderTabContent()}
      </Box>
    </Box>
  );
};

const pageContainerSx: SxProps<Theme> = {
  width: '100%',
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  overflowY: 'auto',
  width: '100%',

  mb: { xs: backrs.spacing['spacing-8xl'], sm: backrs.spacing['spacing-none'] },
});
